
import { computed, defineComponent, onMounted, PropType, reactive, Ref, ref, toRefs, watch } from 'vue'
import ProductNumberIcon from '@/components/Icons/ProductNumberIcon.vue'
import PrintIcon from '@/components/Icons/PrintIcon.vue'
import AddIcon from '@/components/Icons/AddIcon.vue'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import Clipable from '@/components/Clipable.vue'
import ProductCardVariant from '@/pages/Channels/components/ProductCardVariant.vue'
import ProductCardComponent from '@/pages/Channels/components/ProductCardComponent.vue'
import StatusIndicator, { StatusType } from '@/components/StatusIndicator.vue'
import StatusIcon from '@/components/StatusIcon.vue'
import { format } from '@/utils/template'
import { DATE_DISPLAY_FORMAT_HOURS, DATE_FORMAT } from '@/constants'
import { CtCatalogue, CtCatalogueProduct, CtProduct, CtStockItem, CtStockLedger } from '@/models/interfaces/content-type'
import SimpleLink from '@/components/SimpleLink.vue'
import CatalogueProductVariant from '@/pages/Manage/Catalogues/components/CatalogueProductVariant.vue'
import PenIcon from '@/components/Icons/PenIcon.vue'
import useContentModule from '@/pages/composables/useContentModule'
import { useStore } from 'vuex'
import { EntryConfig, UUID } from '@Protium/vue-ozone-utils'
import _, { debounce } from 'lodash'
import ChevronLeftIcon from '@/components/Icons/ChevronLeftIcon.vue'
import { SkuCtrl } from '@/pages/Manage/Catalogues/utils'
import Barcode from '@/components/Barcode.vue'
import BarcodeIcon from '@/components/Icons/BarcodeIcon.vue'
import DButton from '@/components/base/DButton.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import ToggleNew from '@/components/ToggleNew.vue'
import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon.vue'

export default defineComponent({
  name: 'ProductCard',
  components: {
    BarcodeIcon,
    Barcode,
    DButton,
    ChevronLeftIcon,
    PenIcon,
    CatalogueProductVariant,
    SimpleLink,
    StatusIcon,
    StatusIndicator,
    ProductCardComponent,
    ProductCardVariant,
    Clipable,
    TrashIcon,
    AddIcon,
    ProductNumberIcon,
    PrintIcon,
    CloseIcon,
    ToggleNew,
    ExternalLinkIcon,
  },
  props: {
    getCatalogueVariantIndex: {
      type: Function,
    },
    catProduct: {
      type: Object as PropType<CtCatalogueProduct>,
    },
    product: {
      type: Object as PropType<CtProduct>,
    },
    variants: {
      type: Array as PropType<Array<CtProduct['sys']['id'] | string>>,
    },
    removing: {
      type: Boolean,
      default: false,
    },
    skuController: {
      type: Object as PropType<SkuCtrl>
    },
    variantDialogActive: {
      type: String as PropType<CtCatalogueProduct['sys']['id'] | string | null>,
    },
    linkDialogActive: {
      type: String as PropType<CtCatalogueProduct['sys']['id'] | string | null>,
    },
    isLinkedToShopify: {
      type: Boolean,
      default: false,
    },
    storeUrl: {
      type: String,
    },
    readonly: {
      type: Boolean
    }
  },
  emits: ['update:cat-product', 'update:variant-dialog-active', 'update:link-dialog-active', 'expand', 'remove-product', 'cancel'],
  setup (props, { emit }) {
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']
    const stockItems = ref<Array<CtStockItem>>([])
    const stockLedger = ref<Record<UUID, number>>({})


    const openMirrorUrl = () => {
      window.open(`https://admin.shopify.com/store/${props.storeUrl}/products/${props.catProduct?.sys?.mirror_id}`, '_blank')?.focus()
    }

    const statusTooltip = computed(() => {
      if (props.catProduct?.meta?.synced_at) {
        if (props.catProduct?.meta?.sync_error?.length) {
          return `Sync error at ${format.date(props.catProduct?.meta?.synced_at, DATE_FORMAT)}: ${props.catProduct?.meta?.sync_error}`
        } else {
          return `Item last synced at ${format.date(props.catProduct?.meta?.synced_at, DATE_FORMAT)} ${String(props.catProduct?.sys?.mirror_id)?.startsWith('#TEMP') ? '(Pending)' : ''}`
        }
      } else {
        return 'Item not yet synced'
      }
    })

    const getStockItems = async (product) => {
      try {
        const comps = product?.components
        if (!comps?.length) return

        const { items }: { items: Array<CtStockItem> } = await $directr.get('stock', [{ select: ['ean', 'title', 'weight', 'dimensions'] }, comps])
        stockItems.value = items
        return items
      } catch (e) {
        throw new Error('getStockItems | could not get stock items')
      }
    }

    const getStockLedgerCount = (product) => {
      // console.log('getStockLedger', product)
      try {
        const comps = product?.components
        if (!comps?.length) return

        comps.forEach(async (element) => {
          const { items }: { items: Array<CtStockLedger | undefined> } = await $directr.get('stock_ledger', [{ field: 'id', method: 'match', match: `${element}` }, { field: 'catalogue', method: 'match', match: `${props.catProduct?.catalogue}` }])
          const sum = items.filter(item => {
            // TODO: filter on catalogue storage-location...
            return !!item
          }).reduce<number>((s, n) => (s + (n?.count || 0)), 0)

          stockLedger.value[element] = sum
        })

      } catch (e) {
        throw new Error(`getStockLedger | could not get stock ledger ${e}`)
      }
    }

    const state = reactive({
      expanded: false,
      status: StatusType,
      statusTooltip: statusTooltip,
    })

    // HOTFIX
    watch(() => props.variantDialogActive, (value) => value === props.catProduct?.sys.id ? emit('update:link-dialog-active', null) : undefined)
    watch(() => props.linkDialogActive, () => props.variantDialogActive === props.catProduct?.sys.id ? emit('update:variant-dialog-active', null) : undefined)

    watch(() => state.expanded, async () => {
      if (state.expanded) emit('expand')
      await getStockItems(props.product)
      getStockLedgerCount(props.product)
    })

    const synced = computed(() => {
      if (!props.catProduct?.active) {
        return 'disabled'
      } else if (props.catProduct?.meta?.synced_at && !props.catProduct?.meta?.sync_error && !String(props.catProduct?.sys?.mirror_id)?.startsWith('#TEMP')) {
        return 'success'
      } else if (props.catProduct?.meta?.sync_error?.length) {
        return 'error'
      } else {
        return 'warn'
      }
    })

    const updateProduct = debounce((catalogueProduct) => emit('update:cat-product', catalogueProduct), 300, { leading: true })
    const updateProductField = (field, value, editing = false, expand = false) => {
      updateProduct({
        ...props.catProduct,
        [field]: value,
        sys: {
          ...(props.catProduct?.sys || {}),
          editing: editing ? true : props.catProduct?.sys?.editing
        }
      })
      expand && (state.expanded || (state.expanded = true))
    }

    const startEditing = ({ expand }) => {
      if (props.readonly) return
      props.catProduct?.sys?.editing || updateProduct({ ...props.catProduct, sys: { ...(props.catProduct?.sys || {}), editing: true } })
      expand && (state.expanded || (state.expanded = true))
    }

    const productContentModule = useContentModule<CtCatalogueProduct>('CatalogueProducts', $store)
    const { model: productModel } = useContentModule<CtProduct>('Products', $store)
    const catalogueProductModel = productContentModule.model
    const productRequestHandler = productContentModule.requestHandler

    const parsedSearchConfig: Ref<EntryConfig> = ref({ ..._.defaultsDeep({ actions: { edit: true, remove: true, search: false, add: true }, buffer: { resolve: 2, select: ['title', 'sku', 'ean', 'components', 'options'] } }) })

    const syncedAt = computed(() => {
      if (props.catProduct?.meta?.synced_at) {
        if (props.catProduct?.meta?.sync_error?.length) {
          return `Sync error at ${format.date(props.catProduct.meta?.synced_at, DATE_DISPLAY_FORMAT_HOURS)}: ${props.catProduct.meta?.sync_error}`
        } else {
          return `Item last synced at ${format.date(props.catProduct.meta?.synced_at, DATE_DISPLAY_FORMAT_HOURS)} ${String(props.catProduct.sys?.mirror_id)?.startsWith('#TEMP') ? '(Pending)' : ''}`
        }
      } else {
        return 'Item not yet synced'
      }
    })

    const pricing_manager_service = computed(() => {
      return props.skuController?.catalogue?.service?.sync?.product?.pricing_manager_service
    })

    const currencySignDict = {
      'EUR': '€',
      'USD': '$',
      'BRL': 'R$'
    }

    const currencySign = computed(() => {
      const cat = props.skuController?.catalogue as CtCatalogue & { currency: string }
      return currencySignDict[cat?.currency || 'EUR']
    })

    return {
      ...toRefs(state),
      state,
      startEditing,
      updateProduct,
      updateProductField,
      productModel,
      catalogueProductModel,
      productRequestHandler,
      parsedSearchConfig,
      open,
      openMirrorUrl,
      close,
      onMounted,
      synced,
      syncedAt,
      statusTooltip,
      format,
      DATE_FORMAT: DATE_DISPLAY_FORMAT_HOURS,
      showBarcode: ref(false),
      stockItems,
      stockLedger,
      pricing_manager_service,
      currencySign
    }
  },
})
